/**
 * ReplayPanel — one open replay tab: a READ-ONLY frozen canvas rendering the
 * recorded tick at the cursor, the read-only sidebar, and the timeline bar.
 *
 * The canvas is the ordinary GraphCanvas in read-only mode (`editable=false`,
 * `running=false`) fed the RECORDED tick at the cursor and overlays
 * precomputed over the loaded ticks (the same pure `deriveOverlays` fold the
 * live pipeline runs — bottleneck hysteresis included). The live engine's
 * `sim.ts step()` is deliberately bypassed: it rejects backwards steps and
 * asserts conservation on every tick, both wrong for a scrubber.
 *
 * Graph resolution is faithful-by-preference: the dataset's arm-time
 * `meta.graphs` snapshot (the structure the run actually ran against) when
 * present, else the project's CURRENT graphs with a drift badge — nodes
 * added after the recording simply have no recorded values.
 *
 * Every inspector here is the LIVE component rendered read-only (`canEdit` /
 * `editable` false) over recorded data — no replay-specific forks:
 *  - Sidebar: metric cards (computeMetrics over the recorded tick), balance
 *    sheet (+ per-group), graph knobs at their RECORDED effective values
 *    (tick.params / the dataset row's params column — same path grammar),
 *    legend. DB knobs are hidden: their per-tick cell values are not part of
 *    the recording, and today's live cells would be a lie.
 *  - DecisionsModal: the same agent-decision log the StatusBar opens, fed the
 *    cursor's retained decisions (last 8 across ticks, exactly the live
 *    retention semantics).
 *  - NodeModal / EdgeModal: double-click a node/edge (they open regardless
 *    of editability) — same shape, disabled, with live-eval scopes built over
 *    the recorded tick + the recorded effective params.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Warning } from '@phosphor-icons/react';

import type { Graph, ScenarioBundle, TickPayload } from '../engine/types';
import { MAIN_GRAPH_ID } from '../engine/types';
import {
  buildBalanceSheet,
  buildGroupBalanceSheet,
  computeMetrics,
  type GroupBalance,
} from '../engine/balance';
import { memberIdsOf } from '../graph/groups';
import { buildNodeScope } from '../graph/scope';
import { GraphCanvas } from '../graph/GraphCanvas';
import type { OverlayState } from '../overlays/annotations';
import { agentDisplayName, toActivityEntries, type AgentActivityEntry } from '../overlays/annotations';
import { agentColor } from '../graph/palette';
import { Sidebar } from '../panels/Sidebar';
import { DecisionsModal } from '../panels/DecisionsModal';
import { NodeModal } from '../panels/NodeModal';
import { EdgeModal } from '../panels/EdgeModal';
import { Resizer } from '../panels/Resizer';
import { usePrompt } from '../panels/PromptDialog';

import { mintReplayId, precomputeOverlays, stepIndex } from './logic';
import type { ReplayDoc } from './types';
import { ReplayTimelineBar } from './ReplayTimelineBar';
import { useReplaySession, type ReplayFetch, type ReplaySession } from './useReplaySession';

const EMPTY_TICK: TickPayload = { step: 0, nodeValues: {}, edgeFlows: {} };
const EMPTY_OVERLAYS: OverlayState = { nodeCues: {}, edgeCues: {}, decisions: [] };

/** Cap on the replay activity-log aggregation (the loaded window is ~2000
 *  steps already; the cap is a defensive bound, same spirit as useSim's
 *  ACTIVITY_MAX). */
const ACTIVITY_MAX = 2000;

interface ReplayPanelProps {
  doc: ReplayDoc;
  projectId: string;
  adapter: ReplayFetch;
  /** The project's current graphs — the fallback when the recording has no
   *  arm-time snapshot (runs recorded before that existed). */
  liveBundles: ScenarioBundle[] | null;
  showOverlays: boolean;
  showArrows: boolean;
  showGrid: boolean;
  showMinimap: boolean;
  /** Whether the replay sidebar renders (the MenuBar's left-sidebar toggle —
   *  the same persisted pref that gates the live sidebar). */
  showSidebar: boolean;
  /** Sidebar width + setter — the SAME persisted state the live sidebar uses,
   *  so both surfaces agree. */
  sidebarWidth: number;
  setSidebarWidth: (size: number) => void;
  /** Persist a (possibly re-saved) doc — the App owns tabs + the file write. */
  onSave: (doc: ReplayDoc) => Promise<void> | void;
  /** Session registration for the App's keyboard layer (the ACTIVE tab only —
   *  ←/→/Space drive the scrubber through this ref; plain ref assignment, no
   *  state mirror, so it can't loop). */
  onSession?: (session: ReplaySession | null) => void;
}

export function ReplayPanel({
  doc,
  projectId,
  adapter,
  liveBundles,
  showOverlays,
  showArrows,
  showGrid,
  showMinimap,
  showSidebar,
  sidebarWidth,
  setSidebarWidth,
  onSave,
  onSession,
}: ReplayPanelProps): JSX.Element {
  const prompt = usePrompt();
  const [saving, setSaving] = useState(false);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [openNode, setOpenNode] = useState<string | null>(null);
  const [openEdge, setOpenEdge] = useState<string | null>(null);
  const [graphId, setGraphId] = useState<string>(MAIN_GRAPH_ID);
  const [showDecisions, setShowDecisions] = useState(false);

  const initialRange = useMemo(
    () =>
      doc.fromStep !== null && doc.toStep !== null
        ? { from: doc.fromStep, to: doc.toStep }
        : undefined,
    [doc.fromStep, doc.toStep],
  );
  const session = useReplaySession(adapter, projectId, doc.runId, initialRange);

  // Re-register on every render (the session object is fresh each time; the
  // registration is a ref assignment upstream). Cleanup with the OLD callback
  // clears the keyboard ref when this tab deactivates or unmounts.
  useEffect(() => {
    onSession?.(session);
    return () => onSession?.(null);
  });

  const snapshotBundles = useMemo(() => {
    const g = session.info?.meta?.graphs;
    return Array.isArray(g) ? (g as ScenarioBundle[]) : null;
  }, [session.info]);
  const bundles = snapshotBundles ?? liveBundles ?? [];
  const bundle = useMemo(
    () => bundles.find((b) => b.id === graphId) ?? bundles.find((b) => b.id === MAIN_GRAPH_ID) ?? bundles[0] ?? null,
    [bundles, graphId],
  );
  const graph: Graph | null = bundle?.graph ?? null;

  const { states } = useMemo(
    () => (graph ? precomputeOverlays(session.orderedRows, graph) : { states: [] as OverlayState[] }),
    [graph, session.orderedRows],
  );

  // The activity-log aggregation (the modal's replay source): fold every
  // loaded row's decisions + agentLogs into one flat entry list — the same
  // `toActivityEntries` fold the live buffer runs per tick, so the modal is
  // source-agnostic. Old recordings (no agent fields) group under "Model".
  const activity = useMemo(() => {
    const out: AgentActivityEntry[] = [];
    for (const r of session.orderedRows) {
      if (out.length >= ACTIVITY_MAX) break;
      out.push(...toActivityEntries(r.tick));
    }
    return out.length > ACTIVITY_MAX ? out.slice(0, ACTIVITY_MAX) : out;
  }, [session.orderedRows]);

  // The timeline's decision marks: one per decision entry, coloured by the
  // authoring agent (the modal's badges share the palette). The bar dedupes
  // same-step colours + offsets colliding agents itself.
  const decisionMarks = useMemo(
    () =>
      activity
        .filter((e) => e.kind === 'decision')
        .map((e) => ({ step: e.step, color: agentColor(e.agent) })),
    [activity],
  );

  const cursorRow = session.cursorRow;
  const tick = cursorRow?.tick ?? EMPTY_TICK;
  const overlays = useMemo(() => {
    if (!cursorRow) return EMPTY_OVERLAYS;
    const i = stepIndex(session.steps, Number(cursorRow.step));
    return session.steps[i] === Number(cursorRow.step) ? (states[i] ?? EMPTY_OVERLAYS) : EMPTY_OVERLAYS;
  }, [cursorRow, session.steps, states]);

  // Effective param values at the cursor — the recorded knob positions. The
  // tick payload's own params (agent deltas included) win; the dataset row's
  // params column (flatten_params of the resolved params — SAME path grammar)
  // is the fallback for older recordings.
  const paramValues = cursorRow?.tick.params ?? cursorRow?.params ?? undefined;

  // The sidebar's read-only bundle: db knobs are filtered out (their per-tick
  // cell values aren't recorded — rendering today's live cells would be
  // misleading), everything else is the recorded bundle verbatim.
  const viewBundle = useMemo(
    () => (bundle ? { ...bundle, knobs: (bundle.knobs ?? []).filter((k) => k.type === 'graph') } : null),
    [bundle],
  );

  // Balance sheet + metric cards + per-group aggregates, all over the
  // RECORDED tick — the same pure functions the live pipeline runs per tick.
  const derived = useMemo(() => {
    if (!bundle) return null;
    const balance = buildBalanceSheet(bundle.graph, tick);
    const metrics = computeMetrics(bundle.graph, tick, bundle.metrics);
    const groupBalance: Record<string, GroupBalance> = {};
    for (const g of bundle.nodeGroups ?? []) {
      groupBalance[g.id] = buildGroupBalanceSheet(bundle.graph, tick, memberIdsOf(g, bundle.graph.nodes));
    }
    return { balance, metrics, groupBalance };
  }, [bundle, tick]);

  const nodeLabel = useCallback(
    (id: string) => graph?.nodes.find((n) => n.id === id)?.label ?? id,
    [graph],
  );
  const agentName = useCallback((id: string) => agentDisplayName(id, bundle), [bundle]);

  const node = openNode ? graph?.nodes.find((n) => n.id === openNode) ?? null : null;
  const edge = openEdge ? graph?.edges.find((e) => e.id === openEdge) ?? null : null;
  const edgeSource = edge ? graph?.nodes.find((n) => n.id === edge.source) ?? null : null;
  const edgeTarget = edge ? graph?.nodes.find((n) => n.id === edge.target) ?? null : null;

  const handleSave = useCallback(async () => {
    const label = await prompt({
      title: 'Save replay period',
      defaultValue: doc.label,
      confirmLabel: 'Save',
    });
    if (label === null) return;
    setSaving(true);
    try {
      await onSave({
        id: doc.id || mintReplayId(label),
        label: label.trim() || doc.label,
        runId: doc.runId,
        fromStep: session.range.from,
        toStep: session.range.to,
        createdAt: doc.createdAt || new Date().toISOString(),
      });
    } finally {
      setSaving(false);
    }
  }, [doc, onSave, prompt, session.range]);

  const handleDownload = useCallback(() => {
    const rows = session.orderedRows.filter(
      (r) => Number(r.step) >= session.range.from && Number(r.step) <= session.range.to,
    );
    // The old replay.json shape ({ticks}) + a provenance envelope — a
    // self-contained snapshot that survives anywhere.
    const payload = {
      meta: {
        runId: doc.runId,
        seed: session.info?.meta?.seed ?? null,
        provenance: session.info?.provenance ?? null,
        fromStep: session.range.from,
        toStep: session.range.to,
        exportedAt: new Date().toISOString(),
      },
      ticks: rows.map((r) => r.tick),
    };
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectId}-${doc.runId}-${session.range.from}-${session.range.to}-replay.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [doc.runId, projectId, session.info, session.orderedRows, session.range]);

  const spanHint = useMemo(() => {
    const info = session.info;
    if (info === null || info.firstStep === null || info.lastStep === null) return null;
    const span = info.lastStep - info.firstStep + 1;
    if (info.rowCount >= span) return null;
    return `${span - info.rowCount} ticks missing (recording gaps or pruned rows)`;
  }, [session.info]);

  if (session.loading) {
    return <div className="replay-panel"><div className="replay-msg">Loading recording…</div></div>;
  }
  if (session.sourcePruned) {
    return (
      <div className="replay-panel">
        <div className="replay-msg">
          <Warning size={18} />
          <div>
            Recording <code>{doc.runId}</code> is gone — its dataset run was pruned
            (retention <code>datasetMaxRuns</code>). Replay needs the recorded ticks.
          </div>
        </div>
      </div>
    );
  }
  if (session.error) {
    return (
      <div className="replay-panel">
        <div className="replay-msg">
          <Warning size={18} />
          <div>{session.error}</div>
        </div>
      </div>
    );
  }
  if (!graph || !bundle) {
    return <div className="replay-panel"><div className="replay-msg">No graph to render.</div></div>;
  }

  return (
    <div className="replay-panel">
      <div className="replay-body">
        {showSidebar && viewBundle && derived && (
          <div className="replay-side" style={{ width: `${Math.max(200, sidebarWidth)}px` }}>
            <Sidebar
              bundle={viewBundle}
              balance={derived.balance}
              metrics={derived.metrics}
              nodeLabel={nodeLabel}
              selectedNodeId={selectedNode}
              onSelectNode={(id) => {
                setSelectedNode(id);
                setSelectedEdge(null);
              }}
              canEdit={false}
              paramValues={paramValues}
              groupBalance={derived.groupBalance}
              selectedGroupId={selectedGroup}
              onSelectGroup={(id) => setSelectedGroup(id)}
            >
              <Resizer axis="x" dir={1} size={sidebarWidth} setSize={setSidebarWidth} />
            </Sidebar>
          </div>
        )}
        <div className="canvas-wrap">
          <GraphCanvas
            graph={graph}
            tick={tick}
            flowsFromModel
            overlays={overlays}
            showOverlays={showOverlays}
            showArrows={showArrows}
            editable={false}
            selectedNodeId={selectedNode}
            selectedEdgeId={selectedEdge}
            onSelectNode={(id) => {
              setSelectedNode(id);
              setSelectedEdge(null);
            }}
            onSelectEdge={(id) => {
              setSelectedEdge(id);
              setSelectedNode(null);
            }}
            onOpenNode={(id) => {
              setOpenNode(id);
              setOpenEdge(null);
              setSelectedNode(id);
              setSelectedEdge(null);
            }}
            onOpenEdge={(id) => {
              setOpenEdge(id);
              setOpenNode(null);
              setSelectedEdge(id);
              setSelectedNode(null);
            }}
            nodeGroups={bundle?.nodeGroups}
            selectedGroupId={selectedGroup}
            onSelectGroup={(id) => setSelectedGroup(id)}
            showGrid={showGrid}
            showMinimap={showMinimap}
            running={false}
            zoomOnDoubleClick={false}
          />
          {/* Provenance badges float over the canvas's top-right corner —
              informational chips, not a chrome strip. */}
          {(!snapshotBundles || spanHint) && (
            <div className="replay-badges">
              {!snapshotBundles && (
                <span className="replay-badge" title="Recorded before arm-time graph snapshots existed — rendering the current graphs; nodes added later have no recorded values.">
                  <Warning size={11} /> structure may have drifted
                </span>
              )}
              {spanHint && <span className="replay-badge" title={spanHint}>{spanHint}</span>}
            </div>
          )}
          {!cursorRow && session.cursor !== null && (
            <div className="replay-loading-tick">loading ticks…</div>
          )}
        </div>
      </div>
      <ReplayTimelineBar
        session={session}
        label={doc.label}
        graphs={bundles.map((b) => ({ id: b.id, label: b.label }))}
        activeGraphId={bundle?.id}
        onSelectGraph={setGraphId}
        onOpenDecisions={() => setShowDecisions(true)}
        decisionMarks={decisionMarks}
        onSave={handleSave}
        onDownload={handleDownload}
        saving={saving}
      />
      <DecisionsModal
        isOpen={showDecisions}
        entries={activity}
        agentName={agentName}
        nodeLabel={nodeLabel}
        onGoToStep={(step) => {
          session.setCursor(step);
          setShowDecisions(false);
        }}
        onClose={() => setShowDecisions(false)}
      />
      {node && (
        <NodeModal
          node={node}
          bundle={bundle}
          values={tick.nodeValues[node.id]}
          flowsFromModel
          scope={buildNodeScope(node.id, bundle, tick, paramValues)}
          editable={false}
          existingIds={graph.nodes.map((n) => n.id)}
          onClose={() => setOpenNode(null)}
          onSetParam={() => {}}
        />
      )}
      {edge && edgeSource && edgeTarget && (
        <EdgeModal
          edge={edge}
          sourceNode={edgeSource}
          targetNode={edgeTarget}
          allEdges={graph.edges}
          knownGroups={
            Array.from(new Set([
              ...(bundle.groups ?? []),
              ...graph.edges.map((e) => e.group).filter((g): g is string => !!g),
            ]))
          }
          currentFlow={tick.edgeFlows[edge.id]}
          flowsFromModel
          scope={{
            x: tick.edgeFlows[edge.id] ?? 0,
            params: Object.fromEntries(
              edge.params.map((p) => [p.key, paramValues?.[`e:${edge.id}.${p.key}`] ?? p.value]),
            ),
            source: Object.fromEntries(
              edgeSource.params.map((p) => [p.key, paramValues?.[`${edgeSource.id}.${p.key}`] ?? p.value]),
            ),
            target: Object.fromEntries(
              edgeTarget.params.map((p) => [p.key, paramValues?.[`${edgeTarget.id}.${p.key}`] ?? p.value]),
            ),
          }}
          editable={false}
          onClose={() => setOpenEdge(null)}
          onSetParam={() => {}}
        />
      )}
    </div>
  );
}
